/**
 * Modal de confirmação do material detectado.
 * Pergunta ao usuário se o material detectado está correto.
 * 
 * Segue padrões de UI do projeto:
 * - Usa componente Button do sistema
 * - Overlay padrão: bg-black/50 backdrop-blur-sm
 * - Container: bg-navy-800 border border-navy-700 rounded-lg
 */

import { X, Check, RefreshCw, Package, Info, Copy } from 'lucide-react';
import { Button } from '../../../ui/Button';
import type { MaterialConfirmModalProps } from '../types';

export function MaterialConfirmModal({
  isOpen,
  detectionResult,
  totalMetrics,
  onConfirm,
  onReject,
  onClose
}: MaterialConfirmModalProps) {
  if (!isOpen || !detectionResult) return null;

  const { rule, confidence, largestObject, largestObjectAreaCm2 } = detectionResult;
  const materialName = rule.produto?.nome_servico ?? 'Desconhecido';
  const dimensoes = rule.dimensoes;

  // OrderObject já tem width/height em CM (com proteção para undefined)
  const objectWidthCm = (largestObject?.width ?? 0).toFixed(1);
  const objectHeightCm = (largestObject?.height ?? 0).toFixed(1);
  const areaCm2 = largestObjectAreaCm2 ?? (largestObject?.width ?? 0) * (largestObject?.height ?? 0);

  // Determinar cor da confiança
  const getConfidenceColor = () => {
    if (confidence >= 70) return 'text-green-400';
    if (confidence >= 40) return 'text-yellow-400';
    return 'text-red-400';
  };

  const getConfidenceBg = () => {
    if (confidence >= 70) return 'bg-green-500';
    if (confidence >= 40) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-navy-800 border border-navy-700 rounded-lg w-full max-w-[360px] shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-3 border-b border-navy-700">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-blue-500/20 rounded-lg">
              <Package className="w-4 h-4 text-blue-400" />
            </div>
            <span className="text-sm font-semibold text-white">Confirmar Material</span>
          </div>
          <Button variant="ghost" size="xs" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Material detectado */}
          <div className="bg-navy-700/50 rounded-lg p-3 border border-navy-600">
            <p className="text-xs text-gray-400 mb-1">Material detectado:</p>
            <p className="text-base font-bold text-blue-400 leading-tight">{materialName}</p>
          </div>

          {/* Barra de confiança */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between text-xs">
              <span className="text-gray-400">Confiança</span>
              <span className={`font-bold ${getConfidenceColor()}`}>{confidence}%</span>
            </div>
            <div className="w-full bg-navy-700 rounded-full h-1.5">
              <div
                className={`h-1.5 rounded-full transition-all ${getConfidenceBg()}`}
                style={{ width: `${confidence}%` }}
              />
            </div>
          </div>

          {/* ✅ NOVO: Card de Metragem Total */}
          {totalMetrics && (
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 space-y-1">
              <p className="text-xs text-gray-400">Metragem Total</p>
              <div className="flex items-center justify-between">
                <p className="text-2xl font-bold text-white">
                  {totalMetrics.totalHeightM.toFixed(2)} m
                </p>
                <Button
                  variant="ghost"
                  size="xs"
                  onClick={() => {
                    navigator.clipboard.writeText(totalMetrics.totalHeightM.toFixed(2));
                  }}
                  aria-label="Copiar metragem"
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}

          {/* Comparação de dimensões */}
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="bg-navy-700/30 rounded p-2">
              <p className="text-gray-500 mb-0.5">Objeto</p>
              <p className="text-white font-medium">{objectWidthCm} × {objectHeightCm} cm</p>
              <p className="text-gray-500">{areaCm2.toFixed(0)} cm²</p>
            </div>
            <div className="bg-navy-700/30 rounded p-2">
              <p className="text-gray-500 mb-0.5">Regra</p>
              <p className="text-white font-medium">
                {dimensoes?.largura_cm ?? '?'} × {dimensoes?.altura_cm ?? '?'} cm
              </p>
              <p className="text-gray-500">
                {dimensoes?.largura_cm && dimensoes?.altura_cm
                  ? `${(dimensoes.largura_cm * dimensoes.altura_cm).toFixed(0)} cm²`
                  : '-'
                }
              </p>
            </div>
          </div>

          {/* Info */}
          <div className="flex items-start gap-2 bg-blue-500/10 border border-blue-500/20 rounded p-2">
            <Info className="w-3.5 h-3.5 text-blue-400 shrink-0 mt-0.5" />
            <p className="text-xs text-blue-200">
              Detectamos este material baseado no maior objeto do documento.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-2 p-3 border-t border-navy-700">
          <Button
            variant="secondary"
            size="sm"
            icon={RefreshCw}
            onClick={onReject}
            className="flex-1"
          >
            Escolher outro
          </Button>
          <Button
            variant="primary"
            size="sm"
            icon={Check}
            onClick={onConfirm}
            className="flex-1"
          >
            Confirmar
          </Button>
        </div>
      </div>
    </div>
  );
}
